import camelcaseKeys from 'camelcase-keys'

import { formatCents } from './general.ts'
import type { CreditGrant, CreditPosition, CreditPurchase } from '../models/useBilling'

/**
 * The rules the credit surface is gated and validated by, kept out of the components so they can
 * be tested (app#145, app#170).
 *
 * ⚠⚠ THE ROUTER RE-CHECKS EVERY ONE OF THESE. Nothing here is a security boundary — `credit` is
 * Owner-only (`ActionManageBilling`), the minimum is enforced in `buyCredit`, and the period
 * maximum is enforced in `purchase_credit` against a row this app cannot see. These exist so a
 * customer gets a sentence instead of a refusal, which is the same reason `setAutoReload`'s pair
 * rule is checked on both sides.
 */

/**
 * The smallest MANUAL purchase the card network will take, in cents — `billing.MinPurchaseCents`.
 *
 * ⚠⚠ IT BOUNDS ONE QUESTION AND IT USED TO BOUND THREE (app#294). This number answers *what will
 * the card network accept*, and nothing else. What an automatic refill is worth charging is
 * `MIN_AUTO_REFILL_CENTS` below, and the two are different by an order of magnitude. Every site
 * that reads this one is a MANUAL purchase — the Buy credit box and the router's matching
 * refusal — and a site that means "a refill rule" reading this constant is the defect
 * nobgp-router#1333 named on the router side.
 *
 * ⚠ Restated here rather than read from the payload because the router does not send it. If it
 * ever moves, this is a second copy that will not notice; the router's own refusal names the
 * number, which is what the customer actually acts on.
 */
export const MIN_PURCHASE_CENTS = 50

/**
 * The smallest AUTOMATIC refill, in cents — the router's `billing.MinRefillCents` ($5.00).
 *
 * ⚠⚠ THE REASON IS NOT THE CARD NETWORK, AND SAYING SO IS A WRONG STATEMENT TO A CUSTOMER ABOUT
 * THEIR OWN MONEY. The network accepts 50 cents quite happily; what we refuse is a charge whose
 * fee is most of itself — on standard pricing the fee on a 50 cent charge is about 63% of it. So
 * this floor is OURS, and every sentence built on it has to give that reason rather than blame
 * Stripe (nobgp-router#1333, decided by Benoit 2026-09-07).
 *
 * ⚠⚠ IT BOUNDS THREE THINGS, all of them automatic: the smallest rule a customer may save
 * (`reload_cents`), the smallest remainder the cap truncation will still buy, and therefore the
 * smallest cap that can ever charge. `MIN_PURCHASE_CENTS` bounds none of them.
 *
 * ⚠ Restated rather than read from the payload for the same reason as `MIN_PURCHASE_CENTS`.
 */
export const MIN_AUTO_REFILL_CENTS = 500

/**
 * The smallest trigger a refill rule may be saved with, in cents — one cent.
 *
 * ⚠ NOT A FLOOR ON MONEY, A FLOOR ON A COMPARISON. `refillDue` is strictly below the trigger and
 * the balance floors at zero, so a trigger of $0.00 saves a rule that can never fire. Migration
 * `0158` refuses it at the column and `src/tools/credit.go` turns that CHECK into a message.
 */
export const MIN_REFILL_TRIGGER_CENTS = 1

/**
 * The largest purchase this panel makes in one press, in cents — $10,000.00 (app#206).
 *
 * ⚠⚠ A FIXED MAXIMUM, DELIBERATELY NOT THE MONTHLY CAP. The cap was the obvious candidate and it
 * cannot be used: nobgp-router#1195 took the cap OFF a manual purchase on purpose — an org at its
 * cap was throttled AND could not buy the credit that would fix it — and the cap DEFAULTS to $0
 * (router#933), so a cap-bounded Buy button would be dead on arrival for every organization that
 * never set one. See `purchaseRefusal`, where the cap's absence is the point.
 *
 * ⚠ IT EXISTS BECAUSE ONE PRESS MOVES MONEY. Buy is off-session, immediate and had no ceiling at
 * all: `$999,999,999` was accepted with the button enabled (app#206, measured on prod). The
 * number is large enough that no ordinary purchase meets it and small enough that a mistyped
 * extra digit does. A customer who needs more buys twice, which the refusal says.
 *
 * ⚠ THE ROUTER DOES NOT ENFORCE IT, so it is not a boundary — it is a guard against a typing
 * mistake, like every other rule in this file.
 */
export const MAX_PURCHASE_CENTS = 1_000_000

/**
 * Above this a purchase asks again, naming the amount and the card — $500.00 (app#206).
 *
 * ⚠ A THRESHOLD AND NOT A DIALOG ON EVERY PRESS. Buying $20 is the ordinary act this panel exists
 * for, and a confirmation in front of all of them teaches the customer to dismiss the one that
 * matters.
 */
export const CONFIRM_PURCHASE_CENTS = 50_000

/**
 * Dollars as typed into a money field, in cents — or null when the box holds no number.
 *
 * ⚠ `Math.round` and not a truncation: 0.29 * 100 is 28.999999999999996 in IEEE 754, and a floor
 * there charges a cent less than the customer typed — or fails the minimum by one cent on a
 * purchase of exactly $0.50.
 *
 * ⚠⚠ IT STILL ROUNDS, AND `purchaseEntryRefusal` IS WHAT STOPS THE ROUNDING BEING SILENT
 * (app#206). A typed $1.005 becomes 101 cents here and charged $1.01, so the Buy path refuses
 * more than two decimal places BEFORE this number reaches the card. The rounding itself cannot go:
 * every value that survives that refusal needs it, because it is what makes 0.29 come out 29.
 *
 * ⚠ IT LIVES HERE RATHER THAN IN THE COMPONENT so the rounding it does is tested. It was a local
 * `toCents` in `CreditBlock`, where nothing could reach it.
 */
export const dollarsToCents = (typed: string): number | null => {
  const n = Number(typed.trim())
  if (typed.trim() === '' || !Number.isFinite(n)) return null
  return Math.round(n * 100)
}

// Whether typed dollars carry a fraction of a cent.
//
// ⚠ NOT A COUNT OF THE CHARACTERS AFTER THE POINT: a `type="number"` field hands back `1e3` as
// readily as `1000`, and a string test would read that exponent as three decimal places. Measuring
// the distance from a whole number of cents is exponent-proof — and the epsilon is what keeps 0.29
// (28.999999999999996) out of a refusal it has not earned.
const subCent = (dollars: number): boolean =>
  Math.abs(dollars * 100 - Math.round(dollars * 100)) > 1e-6

/**
 * Whether to render the credit surface at all.
 *
 * ⚠⚠ IT ASKS `creditBilled`, NEVER `creditEnabled`. The second is the organization's own switch
 * and defaults to TRUE, so keying on it would render a live-looking balance on a deployment that
 * invoices in arrears — where nothing spends the balance and every purchase is refused
 * `failed_precondition`. Measured on prod 2026-09-02: `NOBGP_CREDIT_BILLING_ENABLED` is false and
 * `credit_enabled` is true, so the two disagree today and the wrong one is the plausible choice.
 *
 * ⚠ FREE SHOWS NO CREDIT SURFACE AT ALL, and that is settled rather than inferred:
 * `docs/PRICING.md` — "The free plan has no credit and never sees this choice" — and the router
 * agrees structurally, since a Free org has never subscribed and `op=buy` answers "there is no
 * card to charge". Hitting the free allowance is the conversion moment; selling credit there
 * removes the reason to subscribe.
 *
 * ⚠ THE ONE EXCEPTION IS `showsCreditReadOnly`, below (app#265): a Free organization that came
 * DOWN from a paid plan still holds the credit it bought, and the billing page shows it that
 * balance without any control. This gate is for the surfaces that offer the controls.
 *
 * ⚠ A null `balanceCents` is the EXEMPT tier — it does not draw on a balance — so it gets no
 * surface either. Null is not zero.
 */
export const showsCredit = (credit: CreditPosition | null, isFree: boolean): boolean =>
  !!credit && credit.creditBilled && !isFree && credit.balanceCents !== null

/**
 * Whether to render the credit surface READ-ONLY — the balance and the purchase list, with no
 * control — for a Free organization (app#265).
 *
 * ⚠⚠ THE CREDIT IS MONEY THE CUSTOMER PAID FOR AND IT DOES NOT EXPIRE. When a paid organization
 * cancels, `DowngradeOrgToFree` moves it to Free and `showsCredit` then hides the balance — $3.50
 * of purchased credit measured on prod, `expired_at` null, with no screen left that showed it.
 * The credit comes back on re-subscription, so nothing is lost; it is INVISIBLE, for exactly the
 * time the customer is deciding whether to come back.
 *
 * ⚠ NO CONTROLS, and that is a decision rather than a shortcut. Free is hard-capped and spends no
 * credit, so a purchase there buys something the organization cannot use until it subscribes
 * again. The caller hides Buy, Auto-refill and the spending switch; this only says whether the
 * figures are worth a section.
 *
 * ⚠ SHOWN ONLY WHEN THERE IS SOMETHING TO SHOW. A Free organization that never bought credit has a
 * $0.00 balance and no history, and a section that says so is the empty section `showsCredit`
 * refuses Free in the first place. A balance above zero, or any purchase this period — a FAILED
 * one included, since it is the customer's only record of the attempt — earns the section.
 *
 * ⚠ `creditBilled` is asked here too, for the reason it is asked above: on an arrears deployment
 * nothing spends the balance, and the read-only view must not light up where the full one does not.
 */
export const showsCreditReadOnly = (credit: CreditPosition | null, purchases: CreditPurchase[] | null): boolean =>
  !!credit &&
  credit.creditBilled &&
  credit.balanceCents !== null &&
  (credit.balanceCents > 0 || (purchases?.length ?? 0) > 0)

/**
 * What is left of this period's AUTOMATIC refill budget, in cents.
 *
 * ⚠⚠ ONE BLOCK, AND IT USED TO BE TWO. A second doc comment sat between the router#933 rule
 * below and this declaration, so only the LAST block bound to the function and that rule was
 * invisible on hover — where `autoReloadCanFire`, the reader that depends on it most, is written.
 * Two doc comments in a row are one comment plus one orphan; keep every rule here in this block.
 *
 * ⚠⚠ IT IS NOT A BOUND ON MANUAL BUYING ANY MORE (nobgp-router#1195). The cap is the ceiling on
 * spending the customer did not authorise one at a time; a manual `op=buy` is an Owner acting
 * deliberately against a card on file and is never refused by it. This figure exists to draw the
 * refill row's progress, not to gate the Buy button.
 *
 * ⚠⚠ A NULL CAP IS ZERO, NOT UNLIMITED (router#933, `docs/PRICING.md` § 5). An organization that
 * has never set a maximum can buy NOTHING until its owner raises it, so its worst-case bill is
 * exactly the flat base. Reading null as "no maximum" would make the default account the
 * unbounded one — the exact inversion router#933 corrected once already.
 *
 * ⚠ Measured against `autoRefilledThisPeriodCents`, never `purchasedThisPeriodCents` — the two
 * differ by exactly the manual purchases, and using the wider one would put manual spending back
 * inside the cap. A router predating #1195 sends no such field; `?? 0` then reports the whole cap
 * as free, which is the safe direction for a READOUT that gates nothing.
 */
export const refillHeadroomCents = (credit: CreditPosition): number =>
  Math.max(0, (credit.spendCapCents ?? 0) - (credit.autoRefilledThisPeriodCents ?? 0))

/**
 * Why this purchase cannot be made, or null when it can.
 *
 * ⚠⚠ THE SPEND CAP NO LONGER APPEARS HERE, AND ITS ABSENCE IS THE POINT (nobgp-router#1195).
 * This used to refuse a purchase that exceeded the period maximum, which produced the corner the
 * redesign removes: an organization at its cap was throttled AND could not buy the credit that
 * would fix it. A manual purchase is a deliberate act by an Owner against a card on file, so the
 * ceiling on UNATTENDED spending has nothing to say about it.
 *
 * ⚠ What remains is the card network's floor, which is not ours to waive — and with the cap gone
 * the amount is the ONLY input, so the position is no longer a parameter. Dropping it rather than
 * ignoring it is what makes a caller that still passes one fail to compile instead of reading as
 * though the balance were still consulted.
 *
 * ⚠⚠ AND A CEILING JOINED IT, WHICH IS NOT THE CAP COMING BACK (app#206). `MAX_PURCHASE_CENTS` is
 * a fixed maximum on ONE press — a guard on a typed digit — and it says nothing about how much an
 * organization may buy in a period, which is the thing #1195 stopped bounding. The two are
 * different objects: this one is the same for every customer, and no customer can raise it.
 *
 * ⚠ THE NON-POSITIVE ARM STILL ANSWERS "Enter an amount." and its callers must not. It is reached
 * from cents, where a negative number and an empty box are the same nothing;
 * `purchaseEntryRefusal` is the layer that can still tell them apart and it names each one.
 */
export const purchaseRefusal = (amountCents: number): string | null => {
  if (!Number.isFinite(amountCents) || !Number.isInteger(amountCents) || amountCents <= 0) {
    return 'Enter an amount.'
  }
  if (amountCents < MIN_PURCHASE_CENTS) {
    return `The smallest purchase is $${(MIN_PURCHASE_CENTS / 100).toFixed(2)} — the card network refuses less.`
  }
  if (amountCents > MAX_PURCHASE_CENTS) {
    return `The largest purchase is ${formatCents(MAX_PURCHASE_CENTS)}. Buy less, or buy again to add more.`
  }
  return null
}

/**
 * Why the amount typed into Buy credit cannot be charged, or null when it can — ONE MESSAGE PER
 * CAUSE (app#206).
 *
 * ⚠⚠ ONE SENTENCE ANSWERED THREE DIFFERENT MISTAKES, AND WAS WRONG ABOUT TWO. `-5` and `0.001`
 * were both met with "Enter an amount." — a sentence that describes an empty box, in front of a
 * customer who had just typed into it. A reason must name a remedy that applies, so the causes
 * are separated here rather than collapsed at the call site.
 *
 * ⚠⚠ IT TAKES THE TYPED STRING, BECAUSE TWO CAUSES DO NOT SURVIVE THE PARSE. An empty box and a
 * box holding `abc` are the same null once parsed, and a sub-cent amount is already rounded away
 * by the time it is cents. `purchaseRefusal` keeps the cents-domain rungs — the parity with the
 * router's own check — and this is the layer above it.
 *
 * ⚠ THE FLOOR MESSAGE IS UNTOUCHED, VERBATIM. It explains WHY the floor exists, which is what
 * makes it the best copy on the panel; every message here is written to that standard.
 *
 * ⚠ ZERO IS NOT GIVEN A MESSAGE OF ITS OWN — it falls to the floor sentence, which is the right
 * one for it: `$0` is refused for the same reason `$0.49` is.
 */
export const purchaseEntryRefusal = (typed: string): string | null => {
  const entry = typed.trim()
  if (entry === '') return 'Enter an amount.'
  const n = Number(entry)
  if (!Number.isFinite(n)) return 'Enter an amount in dollars, like 20 or 19.99.'
  if (n < 0) return 'A purchase cannot be negative. Enter the amount to add to the balance.'
  if (subCent(n)) return 'Enter at most two decimal places — the card is charged in whole cents.'
  const cents = dollarsToCents(entry)
  // Unreachable: every value `dollarsToCents` answers null for is refused above. It is spelled
  // for the compiler, which cannot see that.
  return cents === null ? 'Enter an amount.' : purchaseRefusal(cents)
}

/**
 * Whether this purchase is large enough to ask again before the card is charged (app#206).
 *
 * ⚠ The caption says "Charged to the card on file, now." and it is accurate — one press, off
 * session, no second look. Above the threshold the customer gets that second look, naming the
 * amount and the method it goes to.
 */
export const needsPurchaseConfirmation = (amountCents: number): boolean =>
  Number.isFinite(amountCents) && amountCents > CONFIRM_PURCHASE_CENTS

/**
 * Why this monthly cap cannot be saved, or null when it can.
 *
 * ⚠⚠ IT EXISTS BECAUSE THE OLD `SpendCap` BLOCK HAD ONE AND THE MOVE TO `CreditBlock` LEFT IT
 * BEHIND (nobgp-router#1195). That block's `save()` returned without calling anything on a
 * negative or non-numeric entry; without a replacement, `-5` reaches `set_spend_cap`, which
 * refuses it (`cap_cents must not be negative`) and the customer meets a red error banner where
 * the field itself should have said so.
 *
 * ⚠ NULL — an empty field — IS NOT A REFUSAL AND IS NOT A ZERO. It means "leave the stored cap
 * alone": the caller does not save an untouched field, and clearing the box must never be a
 * silent way to drop an organization's ceiling to $0. Type 0 to mean zero.
 */
export const spendCapRefusal = (capCents: number | null): string | null => {
  if (capCents === null) return null
  if (!Number.isFinite(capCents) || !Number.isInteger(capCents) || capCents < 0) {
    return 'The monthly cap must be zero or more.'
  }
  return null
}

/**
 * A refusal of the auto-refill pair: the message, and WHICH FIELDS it is about (app#301).
 *
 * ⚠⚠ THE FIELDS ARE PART OF THE ANSWER BECAUSE ONE STRING FOR THE PAIR MARKED BOTH BOXES. A
 * trigger of `0` beside a valid `$20` used to paint the `$20` red and print the trigger's
 * sentence under it, telling the customer to fix a value that was already correct. Two of the
 * refusals below are genuinely about the pair and name both fields; the other two name one.
 *
 * ⚠ `fields[0]` is the field to put the cursor in — `armRefusal` reads it rather than
 * re-deriving the rule, which is how the two used to be able to disagree.
 */
export type RefillField = 'trigger' | 'reload'
export type AutoReloadRefusal = { fields: RefillField[]; message: string }

/**
 * Why this auto-reload rule cannot be saved, or null when it can.
 *
 * ⚠⚠ BOTH OR NEITHER. A trigger with no amount is a rule that can never act, an amount with no
 * trigger is one that never fires, and both read as "auto-reload is on" to anyone looking at one
 * field. The router refuses the half-set pair and migration 0144's CHECK refuses the row.
 *
 * ⚠⚠ A ZERO TRIGGER IS NOW REFUSED, AND THIS COMMENT USED TO SAY THE OPPOSITE. It was legitimate
 * while the sweep fired at `balance <= trigger`; nobgp-router#1195 makes that STRICTLY BELOW so
 * the control can read "When below", and the balance floors at zero — so a zero trigger is a rule
 * that saves and can never fire. Migration `0158` refuses it at the column. "Buy only once the
 * balance is empty" is still available and is a trigger of ONE CENT.
 */
export const autoReloadRefusal = (
  triggerCents: number | null,
  reloadCents: number | null,
): AutoReloadRefusal | null => {
  if (triggerCents === null && reloadCents === null) return null // switching it off
  if (triggerCents === null || reloadCents === null) {
    return {
      fields: ['trigger', 'reload'],
      message: 'Set both a trigger and an amount, or clear both to switch auto-refill off.',
    }
  }
  if (triggerCents < MIN_REFILL_TRIGGER_CENTS) {
    return {
      fields: ['trigger'],
      message: 'The trigger must be at least $0.01 — a balance cannot fall below zero.',
    }
  }
  if (reloadCents < MIN_AUTO_REFILL_CENTS) {
    return {
      fields: ['reload'],
      message:
        `Each automatic refill must be at least ${formatCents(MIN_AUTO_REFILL_CENTS)} — a smaller one ` +
        `costs more in card fees than it is worth.`,
    }
  }
  return null
}

/**
 * What one auto-refill box says under itself, or null when it says nothing (app#301).
 *
 * ⚠⚠ IT IS HERE RATHER THAN IN THE COMPONENT BECAUSE THE COMPONENT HAS NO TEST. `CreditBlock`
 * used to decide this inline, with `!!reloadRefusal` for BOTH boxes — so the one rule that
 * matters, *which field is this refusal about*, lived in the one file nothing could assert
 * against. It is three terms and all three have been wrong at once.
 *
 * ⚠ `typed === ''` is the untouched-box guard and belongs to the rule, not to the caller: an
 * empty pair is how auto-refill is switched OFF, and shouting at an empty box is how a customer
 * clearing the rule gets told they are wrong to.
 */
export const refillFieldMessage = (
  refusal: AutoReloadRefusal | null,
  field: RefillField,
  typed: string,
): string | null => (typed !== '' && refusal?.fields.includes(field) ? refusal.message : null)

/** Which field blocks arming auto-refill, and what to say about it. */
export type ArmBlock = { field: 'trigger' | 'reload' | 'cap'; message: string }

/**
 * What stops the auto-refill switch from being turned on, or null when nothing does.
 *
 * ⚠⚠ IT NAMES A FIELD BECAUSE A DISABLED SWITCH NAMES NOTHING. The switch used to be `disabled`
 * until the two numbers were valid, which made Off a one-way door for any organization with no
 * stored rule: the boxes seed empty, so the control an Owner reaches for is dead on arrival and
 * the caption under it is the only thing that says why (Benoit, 2026-09-03, measured on prod —
 * "I can change the use credit slider, but not the auto-refill slider"). The switch is live now,
 * and a press that cannot arm the rule puts the cursor in the field that has to change.
 *
 * ⚠⚠ A $0 CAP DOES NOT BLOCK ARMING, AND IT USED TO — Benoit's decision, 2026-09-03, made with
 * the shipped panel in front of him: *"we should let it slide, it does look weird otherwise and
 * there may be prior values in the fields"*. A cap of $0 does bound automatic refills to nothing
 * (router#933 — an unset cap IS zero, and that is the default), so a rule armed under one cannot
 * fire yet; but refusing there made the SWITCH the thing that says so, and a switch that will not
 * move is the defect this whole line of work exists to remove. It is a WARNING under an On
 * switch now, not a rung of refusal — `CreditBlock`'s caption carries it.
 *
 * ⚠ AN INVALID CAP STILL BLOCKS, and that is a different question from a useless one. Arming
 * saves a CHANGED cap before the rule, so a cap the router would refuse (`-5`) has to stop the
 * press here or the sequence fails halfway, with the cap rejected and the rule armed behind it.
 *
 * ⚠ The pair rule stays in `autoReloadRefusal`, which is what the router and migration `0158`
 * agree with; this only decides which of the two fields to point at.
 */
export const armRefusal = (
  triggerCents: number | null,
  reloadCents: number | null,
  capCents: number,
): ArmBlock | null => {
  if (triggerCents === null) return { field: 'trigger', message: 'Set the balance to buy below.' }
  if (reloadCents === null) return { field: 'reload', message: 'Set how much to buy.' }
  const pair = autoReloadRefusal(triggerCents, reloadCents)
  // ⚠ The field comes from the refusal, not from a second copy of its rule. This line used to
  // re-test `triggerCents < 1` to pick a field, so a change to either floor could put the cursor
  // in a box the message was not about (app#301).
  if (pair) return { field: pair.fields[0], message: pair.message }
  const cap = spendCapRefusal(capCents)
  if (cap) return { field: 'cap', message: cap }
  return null
}

/** Whether auto-reload is on: both halves present, which is the only state the column allows. */
export const autoReloadOn = (credit: CreditPosition): boolean =>
  credit.autoReloadTriggerCents !== null && credit.autoReloadReloadCents !== null

/**
 * The smallest thing THIS rule's next automatic refill can be, in cents.
 *
 * ⚠⚠ IT IS `min(amount, MIN_AUTO_REFILL_CENTS)` AND NOT THE FLOOR, BECAUSE OF THE GRANDFATHERED
 * RULES. The floor bounds the TRUNCATION and nothing else: the router's `refillAmount` buys the
 * amount in full whenever the room covers it, and only a TRUNCATED remainder has to clear the
 * floor. So a rule saved at $3.00 before nobgp-router#1333 still buys $3.00 out of $4.00 of room,
 * and `RefillHeadroom` applies exactly this `min` for exactly this reason — the two must agree,
 * because the sentence a customer reads and the decision the sweep makes are the same claim.
 *
 * ⚠⚠ THOSE RULES ARE NOT HYPOTHETICAL. Migration `0158` constrains a stored amount only to
 * `> 0`, and nothing rewrote the rows that were saved under the old floor — so a rule under
 * $5.00 is a legal, live rule that still fires. Asking the flat floor of one tells its owner
 * "nothing more is bought" on the sweep that is about to buy for them, which is the wrong
 * statement about their money that app#294's split would otherwise have introduced.
 *
 * ⚠ A rule that is not STORED has no amount and answers the full floor. That is the smallest
 * rule that could be saved, which is the right question for a switch pressed but not yet written.
 */
export const refillFloorCents = (credit: CreditPosition): number =>
  Math.min(credit.autoReloadReloadCents ?? MIN_AUTO_REFILL_CENTS, MIN_AUTO_REFILL_CENTS)

/**
 * Whether the stored auto-reload rule can still BUY anything this period.
 *
 * ⚠⚠ ON IS NO LONGER THE SAME QUESTION AS WILL-FIRE, and it became a common state on
 * 2026-09-03 (app#195). The switch used to refuse to arm under a $0 cap; it arms now, and a $0
 * cap is the DEFAULT for every organization that never set one (router#933) — so the ordinary
 * outcome of a customer pressing the switch is a rule that reads On and `refillHeadroomCents`
 * bounds to nothing. Every reader that asked `autoReloadOn` to mean "the balance looks after
 * itself" has been wrong about that customer's money since, which is why this is a named rule
 * here rather than a `> 0` at each call site.
 *
 * ⚠ It answers for THIS PERIOD, so a cap that is merely spent reads the same as a cap of $0.
 * That is the honest answer to the only question the callers ask — is auto-reload going to top
 * this balance up before the customer is throttled again — and the remedy is the same one: raise
 * the cap.
 *
 * ⚠ BOTH SURFACES ASK IT NOW. `BillingBanner` took it in app#195, and `CreditBlock`'s caption
 * kept a narrower `capCents === 0` test for one release — so a spent cap was named on one screen
 * and promised as a live purchase on the other. The caption still branches its WORDS on the $0
 * cap, to print that figure; what may fire is this rule on both.
 *
 * ⚠⚠ THE BOUNDARY IS THE REFILL FLOOR AND IT USED TO BE `> 0` (app#204). `refillAmount`
 * (router#1195) truncates the last refill to whatever is left of the cap and then GIVES UP when
 * that remainder is under `MIN_AUTO_REFILL_CENTS` — so a cap with $4 of headroom buys nothing at
 * all against a $20 rule, and `> 0` answered that it would. ⚠ It asked `MIN_PURCHASE_CENTS`
 * until app#294, which made the dead band ten times narrower than it really is: a $3 cap read as
 * able to fire. That is app#204's $0.25 cap row: the panel promised "Buys $20.00 whenever the
 * balance falls below $10.00" against a cap that can never charge, here and on `BillingBanner` at
 * once. The remedy the banner names — raise the cap — is the right one in that band, which is
 * why both surfaces move together.
 *
 * ⚠⚠ AND IT IS `refillFloorCents`, NOT THE FLOOR ITSELF. A rule saved under the floor still
 * buys its full amount, so asking the flat $5.00 would kill a live grandfathered rule on the
 * screen while the sweep kept buying for it — see `refillFloorCents` for why the router applies
 * the same `min`.
 */
export const autoReloadCanFire = (credit: CreditPosition): boolean =>
  autoReloadOn(credit) && refillHeadroomCents(credit) >= refillFloorCents(credit)

/**
 * How long to wait after a settings write before re-reading the position, so the automatic refill
 * that write may have permitted is on screen (nobgp-router#1328).
 *
 * ⚠⚠ THE ROUTER NOW ATTEMPTS THE REFILL A SETTINGS WRITE PERMITS, AND IT DOES SO ASYNCHRONOUSLY.
 * `set_spend_cap` used to write the column and return; a raised maximum bought nothing until the
 * next 15-minute sweep. It now runs the sweep's own per-org decision out of band — after a ~2 s
 * settle of its own, because this panel's Save writes the cap and then the rule as two calls and
 * the router must not decide on the first alone.
 *
 * ⚠⚠ WITHOUT THIS THE FIX IS INVISIBLE ON THE SCREEN THAT ASKED FOR IT. The Save's own re-read
 * (`rereadRefillState`) runs the moment the writes return, which is BEFORE the router has decided
 * — so the customer would press Save, see the same empty balance and the same "refills have
 * reached the cap" sentence, and learn that their money arrived only by reloading the page.
 *
 * ⚠ It is a WAIT and not a poll. The refill either lands inside this window or it does not, and a
 * customer whose card is inside its decline back-off must not have this panel retrying on their
 * behalf. One extra read; the period is on screen either way.
 *
 * ⚠ Generous on purpose: the router's own settle plus a Stripe round trip, with room to spare. Too
 * short reads the position before the purchase settles and shows the same stale figures this
 * exists to replace, which is the failure that costs something. Too long costs one late read.
 */
export const REFILL_REREAD_MS = 6_000

/**
 * Whether writing this cap can newly permit an automatic refill — the app's half of the router's
 * own trigger (nobgp-router#1328), so the two cannot disagree about when a refill is coming.
 *
 * ⚠ RAISED, and nothing else. Lowering the maximum, or resetting it to the $0 default, REMOVES
 * permission to spend and can buy nothing; `null` is the tightest bound there is (router#933) and
 * resolves to 0 on both sides.
 */
export const capPermitsRefill = (storedCents: number | null, nextCents: number | null): boolean =>
  (nextCents ?? 0) > (storedCents ?? 0)

/**
 * Whether writing this rule can newly permit an automatic refill.
 *
 * ⚠ ARMED, and nothing else. Both null switches auto-refill OFF, which can permit nothing. A
 * re-arm with the same numbers counts: the router fires there too and its guards absorb it, and
 * the app asking a narrower question than the router would leave the one case where a refill
 * lands and the panel never looks.
 */
export const rulePermitsRefill = (triggerCents: number | null, reloadCents: number | null): boolean =>
  triggerCents !== null && reloadCents !== null

/**
 * What a STORED rule will actually buy next when the cap truncates it — or null when it buys the
 * full amount the rule asks for (app#222).
 *
 * ⚠⚠ THE STORED CAPTION PROMISED THE FULL AMOUNT UNDER A CAP THAT CANNOT PAY IT. Measured on prod
 * 2026-09-03 with $60.00 already refilled and the cap saved at $70: the panel read "Buys $20.00
 * whenever the balance falls below $10.00" while only $10.00 was left. `refillAmount`
 * (router#1195) truncates the refill to the remainder, so the true figure was $10.00.
 *
 * ⚠⚠ THE CORRECT SENTENCE ALREADY EXISTED AND WAS HIDDEN AT THE MOMENT IT BECAME TRUE. While the
 * cap was typed and unsaved, `refillOutlook` said "The cap leaves $10.00 this month, so the next
 * refill buys $10.00 and not $20.00" — and pressing Save removed it, because that warning compares
 * the DRAFT against what is stored and there is no difference left once they agree. So the panel
 * warned while the truncation was a prediction and went silent when it became a fact.
 *
 * ⚠ THE DRAFT/STORED SPLIT IS NOT THE BUG AND IS NOT WIDENED. app#204 made `refillOutlook` read
 * the draft deliberately, and the caption describe what is stored. This is the stored side
 * answering the same question for itself.
 *
 * ⚠⚠ IT ANSWERS ONLY FOR A RULE THAT CAN STILL FIRE. `autoReloadCanFire` is false for a $0 cap, a
 * cap under the card floor and a cap already spent — three states the caption ladder already has
 * its own sentences for, each naming a different remedy. Answering here as well would put two
 * sentences under one switch, and this is the case that FALLS THROUGH that ladder: a rule that
 * fires, and buys less than it says.
 *
 * ⚠ Returns the amount BOUGHT, not the shortfall. The caption leads with what happens.
 */
export const storedRefillTruncation = (credit: CreditPosition): number | null => {
  if (!autoReloadCanFire(credit)) return null
  const asked = credit.autoReloadReloadCents
  if (asked === null) return null
  const room = refillHeadroomCents(credit)
  return room < asked ? room : null
}

/** What the cap in front of the customer does to the rule beside it, and what to say about it. */
export type RefillOutlook = {
  kind: 'cap_zero' | 'cap_below_floor' | 'cap_used' | 'truncated'
  message: string
}

/**
 * What the monthly cap being LOOKED AT will let auto-refill buy, or null when it buys the whole
 * amount the rule asks for (app#204).
 *
 * ⚠⚠ IT READS THE DRAFT, AND THAT IS THE WHOLE OF app#204. `armRefusal` and the caption under the
 * switch describe what is STORED — correctly — so an owner learned that a $0 cap buys nothing by
 * saving it first. Four caps measured on prod (0, $0.25, $5 against a $20 rule, and $15 against
 * $20 already refilled) all left Save enabled with nothing said.
 *
 * ⚠⚠ IT WARNS AND MUST NOT BLOCK SAVE. Parking a rule under a $0 cap is a real need: the OFF
 * switch destroys the stored numbers (app#205), so the cap is how a customer stops refills without
 * losing them. Benoit reversed the same refusal on the switch itself for the same reason — see
 * `armRefusal`, where a $0 cap warns and does not block.
 *
 * ⚠⚠ A $5 CAP UNDER A $20 RULE IS NOT A DEAD RULE, AND app#204's TABLE READS AS THOUGH IT WERE.
 * `refillAmount` truncates the refill to what is left, so that rule buys $5.00 — less than asked,
 * which is worth saying, and NOT nothing, which would be a wrong statement about the money.
 *
 * ⚠ THE FLOOR IS WHERE TRUNCATION STOPS. A remainder under `MIN_AUTO_REFILL_CENTS` is not worth
 * charging — the card fee would be most of it — so the last few dollars of any cap are
 * unreachable, and a cap below the floor can never buy anything however large the rule is.
 *
 * ⚠ A CAP ALREADY REFILLED GETS ITS OWN SENTENCE, and it is the subtlest of the four: that rule is
 * dead for the REST OF THIS PERIOD only. The $0 wording offers "raise the cap" as the one remedy
 * and would not say that this one starts working again by itself.
 *
 * ⚠ AN ILLEGAL CAP IS NOT ITS BUSINESS. A negative or non-numeric cap is `spendCapRefusal`'s
 * refusal and blocks Save; answering here as well would put two sentences under one field.
 */
export const refillOutlook = (
  reloadCents: number | null,
  capCents: number | null,
  refilledCents: number,
): RefillOutlook | null => {
  // Half a rule says nothing about the cap — `armRefusal` names the field that is missing.
  if (reloadCents === null || capCents === null) return null
  if (!Number.isFinite(capCents) || capCents < 0) return null
  // ⚠ AN ILLEGAL REFILL AMOUNT IS NOT ITS BUSINESS EITHER, for the reason an illegal cap is not:
  // `autoReloadRefusal` already names that field, and every message here PRINTS the amount. A
  // typed `-5` or `0.10` would otherwise be read back as "Raise it to -$5.00 to refill in full",
  // which is an instruction nobody can act on.
  if (!Number.isFinite(reloadCents) || reloadCents < MIN_AUTO_REFILL_CENTS) return null
  const room = capCents - refilledCents
  if (capCents === 0) {
    return {
      kind: 'cap_zero',
      message: `A $0.00 monthly cap buys nothing. Raise it to ${formatCents(reloadCents)} to refill in full.`,
    }
  }
  // ⚠⚠ THE FLOOR IS ASKED BEFORE THE SPENT CAP, AND THE ORDER IS THE MESSAGE. A cap under the
  // refill floor can never charge, in this period or the next, so "nothing more is bought until the
  // period resets" would promise a refill that never comes. The cap-used sentence belongs only to
  // a cap that is legal and merely spent.
  if (capCents < MIN_AUTO_REFILL_CENTS) {
    return {
      kind: 'cap_below_floor',
      message:
        `A cap below ${formatCents(MIN_AUTO_REFILL_CENTS)} buys nothing — an automatic refill that small ` +
        `costs more in card fees than it is worth. Raise it to ${formatCents(reloadCents)} to refill in full.`,
    }
  }
  if (room < MIN_AUTO_REFILL_CENTS && refilledCents > 0) {
    return { kind: 'cap_used', message: capUsedSentence(capCents, refilledCents) }
  }
  if (room < reloadCents) {
    return {
      kind: 'truncated',
      message:
        `The cap leaves ${formatCents(room)} this month, so the next refill buys ${formatCents(room)} and not ` +
        `${formatCents(reloadCents)}.`,
    }
  }
  return null
}

/**
 * The sentence for a monthly cap that this period's automatic refills have used up — what the cap
 * bounds, what happens now, and the remedy (app#204, app#285).
 *
 * ⚠⚠ ONE DEFINITION, TWO SURFACES. `refillOutlook` says it under the cap field with the refilled
 * figure in front ("$20.00 of the $15.00 cap is already refilled this month, so …"), and
 * `BillingBanner` says it on a `cap_100` alert, where the refilled figure is not on the payload
 * and the lead names the cap alone. The tail is the same words in both places because it is the
 * same fact: under credit the cap bounds AUTOMATIC REFILLS and nothing else (router#1195, #1264,
 * #1279) — it pauses no usage, stops no machine, slows nothing. The banner used to say "new
 * billable usage is paused across the board", which was the retired arrears enforcement, and it
 * drifted because the sentence lived in one place and the banner in another (app#285).
 *
 * ⚠⚠ "SLOWS NOTHING" IS TRUE OF THE PREDICATE AND NOT OF THE CAUSE (nobgp-router#1328). The cap
 * appears in neither `throttleCreditSQL` nor `CheckBillingBlock`, so nothing reads it to slow or
 * refuse anything — and for an organization with a rule armed it is nonetheless one hop upstream
 * of what does: cap exhausted, refill truncated to nothing, balance drains, throttled. The
 * sentence above already carries that hop ("nothing more is bought until the period resets"),
 * which is why it needs no rewrite; the CLAIM in this comment is what needed the qualifier, and
 * it is the claim that hid the missing action for a release cycle. Do not restore the bare form.
 *
 * ⚠ "until the period resets" is a promise, and it is only true of a cap an automatic refill is
 * worth charging against — `refillOutlook` asks the floor first for that reason. This function
 * does not ask it: its callers already have.
 *
 * ⚠⚠ NEITHER ARM IS `capExhaustedClause`, AND THAT IS NOT DRIFT (app#303). The no-figure arm is
 * reached only from a router `cap_100` row, whose predicate is `SpendCapExceeded` —
 * `refilled >= cap` — so "have reached this month's cap" is exactly what happened and there is no
 * remainder to name. The two-figure arm already prints both numbers and claims no exhaustion.
 * What app#303 fixed were the two SHORT sentences the caption and the banner had invented for
 * themselves, and those now read the clause.
 */
export const capUsedSentence = (capCents: number, refilledCents: number | null = null): string =>
  (refilledCents === null
    ? `Your automatic refills have reached this month’s cap of ${formatCents(capCents)} — `
    : `${formatCents(refilledCents)} of the ${formatCents(capCents)} cap is already refilled this month, so `) +
  'nothing more is bought until the period resets. Raise the cap to refill again this month.'

/**
 * Why an armed rule under a LEGAL cap buys nothing more this period, as the clause both surfaces
 * embed (app#303).
 *
 * ⚠⚠ "FULLY USED" WAS FALSE BY UP TO $4.99 AND IT USED TO BE FALSE BY $0.49. `autoReloadCanFire`
 * asks the refill floor, not zero, so a cap with real money left is dead — and `CreditBlock` said
 * "the $10.00 cap is fully used" and `BillingBanner` "its monthly cap has nothing left this month"
 * against a customer whose own refill history showed $7.00 of $10.00. app#294 raised the floor
 * from $0.50 to $5.00, which is right, and made both sentences ten times wronger.
 *
 * ⚠⚠ THE TWO ARMS ARE NOT A COMPROMISE. A remainder of exactly nothing IS fully used, and saying
 * so is shorter and truer than naming a $0.00 remainder; anything above nothing has to say what
 * is left, because the customer can see that figure and we cannot claim it is gone. Both arms
 * state a number that can be checked against the refill list.
 *
 * ⚠ THE FLOOR IN THE SECOND ARM IS ALWAYS THE RIGHT NUMBER, even for a rule grandfathered under
 * it. Its callers are reached only when `refillHeadroomCents < refillFloorCents`, and that floor
 * is `min(amount, MIN_AUTO_REFILL_CENTS)` — so the remainder is under $5.00 in every case that
 * gets here, whatever the rule asks for.
 *
 * ⚠ IT SAYS NOTHING ABOUT THE REMEDY. Both callers name their own, and they differ: the caption
 * offers the rule it would buy, the banner offers the cap.
 */
export const capExhaustedClause = (credit: CreditPosition): string => {
  const capCents = credit.spendCapCents ?? 0
  const room = refillHeadroomCents(credit)
  return room === 0
    ? `${formatCents(capCents)} monthly cap is fully used`
    : `${formatCents(capCents)} monthly cap has ${formatCents(room)} left, under the ` +
      `${formatCents(MIN_AUTO_REFILL_CENTS)} an automatic refill has to be worth`
}

/** The two spend-cap alert kinds the router writes (`src/billing/alerts.go`). */
export type CapAlertKind = 'cap_80' | 'cap_100'

/**
 * What the banner says on a `cap_80` / `cap_100` alert row, or null when there is nothing true to
 * say (app#285).
 *
 * ⚠⚠ NULL WHEN THE ORG HAS NO CAP, WHATEVER ROW IT HOLDS. `capCents` is `organizations
 * .spend_cap_cents` as the app read it — the payload — and the alert row is the router's sweep
 * from up to a day earlier. The router's ladder read an UNSET cap as $0 and wrote `cap_100` for an
 * org that refilled under a cap and then removed it (router#1322, measured 2026-09-06 on prod):
 * the banner said "you've reached your monthly spend cap" over a credit block showing no cap at
 * all. The router half stops writing that row; this is the app's guard until it ships, and after
 * — a stale row is not trusted over the payload. A $0 cap is "no bound" here too: there is
 * nothing to be at 80% or 100% of, which is the router's own rule for the 80% rung, and a `cap_100`
 * over a $0 cap is the same stale row one step over — the refill was bought under an earlier cap
 * (migration `0158` truncates one to nothing under $0), and "$0.00" is not a figure the customer
 * reached.
 *
 * ⚠⚠ THE FLOOR IS THE PREDICATE, NOT `> 0`, because `capUsedSentence` above says its callers must
 * ask it. A cap UNDER `MIN_AUTO_REFILL_CENTS` can never be charged, so it can never have bought the
 * refills a `cap_100` row claims it reached — the refills came from an earlier, higher cap, which
 * is the $0 case one notch over — and "nothing more is bought until the period resets" promises a
 * reset that changes nothing, since the next period refuses the same refill. `refillOutlook` has a
 * separate `cap_below_floor` sentence for exactly this and renders it in the credit block directly
 * under this banner, so a `> 0` test here would put the two surfaces back into disagreement over a
 * $0.25 cap after unifying them everywhere else. `spendCapRefusal` accepts any integer ≥ 0, so a
 * sub-floor cap is reachable, not hypothetical.
 *
 * ⚠ It says only what the cap bounds. The `cap_80` rung fires at `refilled ≥ 80% of the cap`, so
 * "at 80%" is the floor of what happened, not the exact figure — the gauge under Usage credit has
 * the exact one.
 */
/**
 * Whether a stored `cap_80` / `cap_100` row still describes what this period's refills reached
 * (app#291) — or null when this viewer cannot tell.
 *
 * ⚠⚠ THE ROW IS A LATCH AND THE CAP IS NOT. A customer at their $5 maximum presses the banner's
 * own **Raise the cap**, sets $50, and the row survives until the ONCE-A-DAY sweep clears it — so
 * for up to a day the banner named a figure they never reached, said refills were stopped while
 * they were running again, and told them to redo the thing they had just done. app#287 made it
 * confidently wrong rather than vaguely wrong by putting the payload's cap into the sentence.
 *
 * ⚠ It mirrors the router's own rungs in integer arithmetic — `refilled*100 >= cap*80` and
 * `refilled >= cap` (`CheckSpendCapThresholds`) — so the two cannot disagree about the edge.
 *
 * ⚠⚠ NULL MEANS "CANNOT SEE", AND IT IS NOT THE SAME ANSWER AS `true`. `credit` is null for a
 * MEMBER, because the tool behind it is Owner-only. The caller decides what to do with that; this
 * function refuses to guess, because guessing `false` would silence a true warning and guessing
 * `true` would restate the latch as fact.
 */
export const capAlertStillReached = (
  kind: CapAlertKind,
  capCents: number | null,
  credit: CreditPosition | null,
): boolean | null => {
  const refilled = credit?.autoRefilledThisPeriodCents
  if (refilled === undefined || capCents === null || !Number.isFinite(capCents)) return null
  return kind === 'cap_100' ? refilled >= capCents : refilled * 100 >= capCents * 80
}

export const capAlertMessage = (kind: CapAlertKind, capCents: number | null): string | null => {
  if (capCents === null || !Number.isFinite(capCents) || capCents < MIN_AUTO_REFILL_CENTS) return null
  return kind === 'cap_100'
    ? capUsedSentence(capCents)
    : `Automatic refills are at 80% of this month’s ${formatCents(capCents)} cap.`
}

/** Why an armed auto-refill rule cannot buy anything. */
export type RefillBlock = 'cap_zero' | 'cap_below_floor' | 'cap_used'

/**
 * Why a STORED, armed rule can buy nothing — or null when it can buy (app#223).
 *
 * ⚠⚠ IT EXISTS BECAUSE TWO SURFACES ANSWERED THIS DIFFERENTLY. `CreditBlock`'s caption splits the
 * three causes and names a different remedy for each; `BillingBanner` had ONE sentence for all
 * three — "its monthly cap has nothing left" — which is true only of the spent cap. For a cap
 * below the refill floor nothing has been taken from it at all: it is too small to be worth
 * charging, not used up, and a customer who checks their refill history will not find the spend that
 * sentence implies.
 *
 * ⚠ The remedy named on the banner (raise the cap) is right for all three, which is why this was
 * filed at medium and not higher. The REASON was wrong, and a reason has to name something the
 * customer can recognise.
 *
 * ⚠⚠ THE ORDER IS THE ANSWER, exactly as in `refillOutlook`, and the two must not be reordered
 * independently. A $0 cap is also below the floor and also has nothing left, so the three tests
 * overlap and only the order separates them: the $0 case prints a figure the customer set, the
 * sub-floor case names the refill minimum, and what is left is a cap that is legal and can buy no
 * more. ⚠ `cap_used` is NOT the same as spent: it is reached whenever the remainder is under the
 * floor, so it covers a $10.00 cap with $3.00 left as well as one with nothing. The banner says
 * which through `capExhaustedClause` (app#303) rather than asserting the harder of the two.
 *
 * ⚠ `refillOutlook` is the DRAFT's version of this question and stays separate. It answers about a
 * cap being typed, this one about the cap that is stored, and app#204 established that split
 * deliberately.
 */
export const refillBlock = (credit: CreditPosition): RefillBlock | null => {
  if (!autoReloadOn(credit)) return null
  if (autoReloadCanFire(credit)) return null
  const cap = credit.spendCapCents ?? 0
  if (cap === 0) return 'cap_zero'
  if (cap < MIN_AUTO_REFILL_CENTS) return 'cap_below_floor'
  return 'cap_used'
}

/**
 * Whether the automatic-refill gauge — "Automatic refills in <month> $X / $Y" and the bar under
 * it — is on the screen at all (app#278).
 *
 * ⚠⚠ THE GUARD app#201 REMOVED, PUT BACK ONE CONDITION NARROWER. The component used to hide the
 * row under a $0 cap, and app#201 un-hid it so that the colour could ask the SWITCH before it
 * warns about the cap: a rule that is On with a $0 or sub-floor cap buys nothing, and that is the
 * moment the customer most needs to see why. The un-hiding kept the line and lost the guard, so
 * an organization with the switch OFF and no cap read "$5.00 / $0.00" after an earlier refill
 * and every new subscriber's first screen read "$0.00 / $0.00" — a fraction with a zero
 * denominator under real money, which reads as OVER CAP about a rule the customer never touched.
 *
 * The rule: hidden only when the switch is off AND no cap is set. There is nothing to measure
 * against. An earlier refill in the period is still listed under "Purchases in <month>", labelled
 * `automatic`, which is the honest place for it. Every other state keeps the gauge:
 *   - switch on, any cap — including $0 and below the card floor, so the warning colour and the
 *     caption beside it still say why nothing is bought (the property app#201 was for);
 *   - switch off, cap set — the cap is a control the customer set, and the gauge says how much of
 *     it the rule used before it was switched off.
 *
 * ⚠ `armIntent` is the switch held On in the UI before a rule is stored (`CreditBlock`'s
 * `switchOn = refillOn || armIntent`). The gauge asks the same question the colour does, so it
 * appears the moment the switch is pressed — otherwise the row would blink in on Save.
 */
export const showsRefillGauge = (credit: CreditPosition, armIntent = false): boolean =>
  autoReloadOn(credit) || armIntent || (credit.spendCapCents ?? 0) > 0

/**
 * How full the automatic-refill gauge is drawn, 0-100.
 *
 * ⚠⚠ IT ANSWERS FOR A $0 CAP RATHER THAN LETTING THE BAR LEAVE THE SCREEN (app#201). The component
 * hid the whole row when the cap was zero — the one moment a customer most needs to see why
 * nothing is bought. A zeroed gauge is information; an absent one is not. It has to be answered
 * here because `refilled / 0` is NaN and `LinearProgress` draws a NaN width as a bar of no
 * defined length.
 *
 * ⚠ A $0 CAP WITH REFILLS ALREADY TAKEN IS DRAWN FULL, not empty. It is reached by lowering the cap
 * mid-period, and "the budget is used up" is the honest reading of it — which is what the warning
 * colour beside it says. ⚠ THE BRANCH IS STILL LIVE after app#278: `showsRefillGauge` hides the
 * row only when the switch is OFF and no cap is set, so a rule that is ON with its cap lowered to
 * $0 after a refill still draws the bar, and this is the value it draws. With the switch off the
 * row is gone and the value is never read.
 */
export const refillGaugePercent = (credit: CreditPosition): number => {
  const cap = credit.spendCapCents ?? 0
  const refilled = credit.autoRefilledThisPeriodCents ?? 0
  if (cap <= 0) return refilled > 0 ? 100 : 0
  return Math.min(100, Math.max(0, (refilled / cap) * 100))
}

/**
 * Money this period counts as purchased that is neither in the balance nor spent, in cents — a
 * purchase that was reserved and never credited (app#201).
 *
 * ⚠⚠ IT IS A RESIDUAL AND NOT A FIELD, BECAUSE THE PAYLOAD CARRIES NO PENDING FIGURE.
 * `credit op=status` reports `purchased_this_period_cents` as "bought OR RESERVED" and
 * `auto_refilled_this_period_cents` as "counting reservations still in flight", and neither says
 * how much is in flight or how old it is; `credit_purchases` is reachable by the `router` role
 * alone (router migration 0149), so the app cannot ask the table either. A router-side
 * `pending_purchase_cents` plus the age of the oldest row is what replaces this — app#201.
 *
 * ⚠⚠ WHY THE ARITHMETIC HOLDS. balance = opening + granted − spent, an opening balance is never
 * negative, and a settled purchase grants its full amount — so purchased − spent − balance is at
 * most zero while every purchase has settled. A POSITIVE residual is money the organization is
 * accounted as having bought and has not received.
 *
 * ⚠ IT IS NOT EXACT AND MUST NOT BE PRINTED AS THOUGH IT WERE THE WHOLE STORY. A GRANT that lapses
 * mid-period leaves a positive residual too, and this counts manual purchases while the refill
 * gauge beside it counts automatic money only — which is the coupling app#201 is about: one manual
 * Buy holds the automatic sweep off for the rest of the month. It answers "is some of this month's
 * money unaccounted for", which is the question three contradictory figures raise.
 *
 * ⚠ A NULL BALANCE IS THE EXEMPT TIER and answers zero: it draws on no balance, so the identity
 * says nothing about it. `showsCredit` already refuses that organization the surface.
 */
export const unsettledPurchaseCents = (credit: CreditPosition): number =>
  credit.balanceCents === null
    ? 0
    : Math.max(0, credit.purchasedThisPeriodCents - credit.spentThisPeriodCents - credit.balanceCents)

/**
 * Money bought this period whose charge has not settled (router#1206, app#201).
 *
 * ⚠⚠ IT IS A FIELD NOW, AND THE RESIDUAL BELOW IT IS THE DEPLOY WINDOW ONLY. `pending_purchase_cents`
 * is a READ of the pending rows; the residual is arithmetic over three totals that happens to
 * equal it in the normal case. The two differ where it matters: a GRANT that lapses mid-period
 * leaves a positive residual with nothing pending, and the residual counts manual purchases while
 * the refill gauge beside it counts automatic money only.
 *
 * ⚠ REMOVE THE FALLBACK once router#1206 is released everywhere. It exists because the app ships
 * independently of the router and may run against one that predates the field — the same window
 * `autoRefilledThisPeriodCents` is optional for. Until then a stale-but-close figure beats none.
 *
 * ⚠ `?? ` and not `||`: zero is a complete answer meaning "nothing in flight", and `||` would
 * discard it and fall back to the residual — which can be positive for the lapsed-grant reason
 * above, so the panel would announce money in flight on a router that had just said there is none.
 */
export const pendingPurchaseCents = (credit: CreditPosition): number =>
  credit.pendingPurchaseCents ?? unsettledPurchaseCents(credit)

/** How long the oldest unsettled purchase has been in flight, and whether that is worrying. */
export type PendingAge = { text: string; stale: boolean }

/**
 * The AGE of the oldest purchase in flight — the half the residual could never carry (app#201).
 *
 * ⚠⚠ MINUTES AND HOURS ARE DIFFERENT SITUATIONS. The router's own field description says so:
 * a reservation is normal and usually brief — the money is held and the card is being charged —
 * while hours mean it needs a person. A screen that says only "money is in flight" leaves the
 * customer unable to tell the ordinary case from the one that has gone wrong, which is the whole
 * reason the instant is on the wire.
 *
 * ⚠ `relativeTime` in `general.ts` is DAY-granular and answers "0 days ago" for every case this
 * function exists to separate. It is right for invoices and wrong here, so this is a second
 * formatter on purpose rather than a widening of that one.
 *
 * ⚠ Returns null for a missing, empty or unparseable stamp — including the deploy window, where
 * the router sends no field at all — so the caller prints the amount with no age rather than
 * "in flight for NaN minutes".
 *
 * ⚠ A FUTURE stamp reads as the youngest age rather than a negative one. Clocks disagree by
 * seconds and a customer must never be told their money has been in flight for -1 minutes.
 *
 * ⚠⚠ EVERY ANSWER STARTS "for", BECAUSE THE CALLER PUTS "in flight" IN FRONT OF IT. A bare "just
 * now" composed to "in flight just now" on the one row this sentence is most likely to be read on
 * — the seconds after a customer presses Buy. The shape of the sentence is the function's, not the
 * component's, so the sub-minute case says "for less than a minute" and the template never has to
 * branch.
 */
export const ONE_HOUR_MS = 60 * 60 * 1000

export const pendingAge = (at: string | null | undefined, now: number = Date.now()): PendingAge | null => {
  if (!at) return null
  const started = new Date(at).getTime()
  if (Number.isNaN(started)) return null
  const ms = Math.max(0, now - started)
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return { text: 'for less than a minute', stale: false }
  if (minutes < 60) return { text: `for ${minutes} minute${minutes === 1 ? '' : 's'}`, stale: false }
  const hours = Math.floor(ms / ONE_HOUR_MS)
  return { text: `for ${hours} hour${hours === 1 ? '' : 's'}`, stale: true }
}

/**
 * Why a paid org on a CREDIT deployment is throttled — the three causes that replace `cap` there.
 *
 * ⚠⚠ THE SPEND CAP DOES NOT THROTTLE UNDER CREDIT, AND app#145's OWN TABLE PREDATES THAT.
 * `throttleCreditSQL` fires when an allowance is exhausted AND either the owner has switched
 * spending off or the money balance is empty; the cap bounds PURCHASES and nothing else. So a
 * banner that keeps saying "you reached your monthly spend cap" on a credit deployment names an
 * object that did not cause the throttle, and sends the customer to raise a limit that will not
 * lift it.
 *
 * ⚠ The causes have DIFFERENT remedies and must not be collapsed. An org with spending switched
 * off HAS credit and chose not to spend it — telling it to buy more is the one instruction that
 * cannot help. An org with an empty balance has the opposite problem.
 *
 * ⚠⚠ THREE STATES, NOT TWO (app#269). `throttled_until` is a fail-open stamp with no clear: the
 * evaluator re-stamps every two minutes and the stamp lives six, so a throttle ends by lapsing.
 * A customer who buys credit while throttled therefore stays throttled for up to six minutes,
 * with a positive balance. The two-way form read that state as `credit_empty` and told them to
 * add credit — the thing they had just done, about money they had just spent. So the balance is
 * asked too: spending on and a positive balance is `credit_landed`, and the only true sentence
 * there is that the limit is lifting. ⚠ Do not "fix" the window by clearing the stamp on
 * purchase; the stamp is deliberately fail-open, and the sentence can be true from the state the
 * app already holds.
 *
 * ⚠ A PENDING PURCHASE IS `credit_landed` TOO. A purchase whose charge has not settled leaves the
 * balance at zero and `pendingPurchaseCents` positive; the customer has still just paid. The state
 * proves a positive balance OR a purchase in flight, and the sentences that read it say only that.
 *
 * ⚠ A null balance is the exempt tier (see `showsCredit`) and reads as `credit_empty`, exactly as
 * it did before. Null is not positive, and a claim about the balance needs a number.
 */
export const creditThrottleCause = (credit: CreditPosition): 'credit_empty' | 'spending_off' | 'credit_landed' => {
  if (!credit.creditEnabled) return 'spending_off'
  if ((credit.balanceCents ?? 0) > 0 || (credit.pendingPurchaseCents ?? 0) > 0) return 'credit_landed'
  return 'credit_empty'
}

/**
 * The grants that make up a MONEY balance, in the order they will be spent (app#146 item 2).
 *
 * ⚠⚠ ONLY THE `money` AXIS. `plan_allowance` grants are denominated in bytes and compute-cents
 * (the `usage_grants_denomination` CHECK forbids them from being money), so folding them in would
 * add bytes to dollars. The balance the block shows is `MoneyBalanceCents`, which reads the money
 * leg alone; the breakdown has to answer for the same number.
 *
 * ⚠ A SPENT grant is as gone as an EXPIRED one, so both are filtered — the rule `liveBoosts`
 * already applies one surface over. Showing a spent grant would explain a balance the customer
 * does not have.
 *
 * ⚠ The RPC's own order is preserved: it is `consume_usage`'s spend order (expiry first, never-
 * expiring last), so the list reads top-to-bottom as the order the money will actually go. Sorting
 * by size or kind would show a plausible sequence that is not the real one.
 */
export const moneyBreakdown = (grants: CreditGrant[], now: number = Date.now()): CreditGrant[] =>
  grants
    .filter(g => g.axis === 'money')
    .filter(g => g.remaining > 0)
    .filter(g => !g.expiresAt || new Date(g.expiresAt).getTime() > now)

/**
 * What to call a KIND on screen — one line per kind now, never per grant (see `creditByKind`).
 *
 * ⚠ The two we gave are named by what they are, because "credit" alone would not tell a customer
 * which part expires.
 *
 * ⚠⚠ "Purchased", NOT "You bought" (Benoit, 2026-09-03). It labels a SUM across every purchase
 * now, and "You bought $41.50" is false of any single act — the customer bought $20.00, then
 * $1.00, then $0.50, then $20.00. A noun states the category; a verb claims an event that did not
 * happen. It also has to sit in a column beside "Promotional", which is a noun.
 */
export const grantLabel = (kind: string): string => {
  switch (kind) {
    case 'purchased':
      return 'Purchased'
    case 'promotion':
      return 'Promotional'
    case 'compensation':
      return 'Service credit'
    default:
      // ⚠ The router owns this vocabulary and can add to it without an app deploy, so an
      // unrecognised kind renders neutrally rather than as `undefined`. Same rule AXIS_NOUN
      // follows in the banner.
      return 'Credit'
  }
}

/** One line of the balance breakdown: a KIND, its total, and the first date any of it lapses. */
export type CreditGroup = { kind: string; label: string; remaining: number; expiresAt: string | null }

/**
 * The balance breakdown, ONE LINE PER KIND (Benoit, 2026-09-03).
 *
 * ⚠⚠ IT WAS ONE LINE PER GRANT, AND A GRANT IS A PURCHASE. Four top-ups produced four rows —
 * "You bought $20.00 does not expire" three times over with a different figure — which is a
 * transaction history, and a bad one: no dates, no order the reader can use, and the same three
 * words repeated down the block. The balance has TWO facts worth a line, and they are the two that
 * BEHAVE differently: what you paid for, which does not expire, and what we gave you, which does
 * and is spent first. Both are properties of the KIND, so the kind is the row.
 *
 * ⚠⚠ THE ORDER IS PRESERVED AND NOT RE-SORTED, and that is what keeps the caption below the block
 * honest. `moneyBreakdown` hands back the RPC's own order, which is `consume_usage`'s spend order
 * — expiring first, never-expiring last — so grouping by FIRST APPEARANCE inherits it. Sorting by
 * size, or by kind name, would print a plausible sequence that is not the one the money follows,
 * and "Credit that expires is spent first" would then describe a list that shows otherwise.
 *
 * ⚠⚠ A KIND'S DATE IS THE SOONEST IN THE GROUP, never the last. Same rule and same reason as
 * `soonestExpiry` in `grants.ts`: with a mixed group the last date promises the whole sum for
 * longer than part of it exists, and the soonest under-promises, which is the safe direction. A
 * kind with nothing dated in it carries null and reads "does not expire", which is TRUE of the
 * whole sum only because every member of the group is undated.
 *
 * ⚠ It starts from `moneyBreakdown`, so the spent and the lapsed are already gone. Do not group
 * raw `creditGrants`: a spent grant would add its zero-value row back, and an expired one would
 * drag a whole kind's date into the past.
 */
export const creditByKind = (grants: CreditGrant[], now: number = Date.now()): CreditGroup[] => {
  const out: CreditGroup[] = []
  const at = new Map<string, number>()
  for (const g of moneyBreakdown(grants, now)) {
    const i = at.get(g.kind)
    if (i === undefined) {
      at.set(g.kind, out.length)
      out.push({ kind: g.kind, label: grantLabel(g.kind), remaining: g.remaining, expiresAt: g.expiresAt })
      continue
    }
    const row = out[i]
    row.remaining += g.remaining
    if (g.expiresAt && (!row.expiresAt || new Date(g.expiresAt).getTime() < new Date(row.expiresAt).getTime())) {
      row.expiresAt = g.expiresAt
    }
  }
  return out
}

/**
 * When a kind's credit lapses, or that it does not.
 *
 * ⚠⚠ UTC, BECAUSE AN EXPIRY IS A BOUNDARY AND NOT A MOMENT — the rule `freshness.ts` states for
 * the billing period. Rendered in the reader's own zone this prints the wrong DAY for everyone
 * west of Greenwich, on a date that decides when their money stops being theirs.
 *
 * ⚠ THE YEAR APPEARS ONLY WHEN IT IS NOT THIS ONE. A promotional grant can run past December, and
 * "expires 15 Jan" beside a balance is a date the reader will assume is the nearer one. Carrying
 * the year unconditionally spends width on a fact that is redundant eleven months in twelve; this
 * is app#225's rule (a renewal date rendered with no year) applied where the same trap exists.
 */
export const grantExpiryLabel = (expiresAt: string | null, now: number = Date.now()): string => {
  if (!expiresAt) return 'does not expire'
  const d = new Date(expiresAt)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear()
  return `expires ${d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })}`
}

// The credit position out of one `credit` reply — the ONE place the wire shape is read, because
// every op returns the same payload and four copies of the same cast is four chances to get it
// wrong once.
//
// ⚠⚠ THE ARGUMENT IS ALREADY THE PAYLOAD. `callTool` unwraps `body[toolName]` (app#173) and this
// tool's payload key is `credit`, the same word as its name, so there is nothing left to reach
// into. Reading `res.credit` here would be `undefined` on every reply.
//
// ⚠ `unwrapToolPayload` returns the ENVELOPE UNCHANGED when that key holds null — which is what a
// `credit: null` answer (no plan row, an org the tool has nothing to say about) looks like on the
// wire. The envelope has no `org_id` and the payload always does, so that is the discriminator: a
// bare truthy check would store a position whose every field is undefined, and `creditBilled`
// being undefined would then read as "arrears" rather than as "no answer".
export const creditPositionFrom = (res: unknown): CreditPosition | null =>
  res !== null && typeof res === 'object' && 'org_id' in res
    ? (camelcaseKeys(res as Record<string, unknown>) as CreditPosition)
    : null

/**
 * The purchase list out of a `credit op=purchases` reply, or null when there is none to show.
 *
 * ⚠⚠ NULL AND [] ARE DIFFERENT ANSWERS AND THE ROUTER DRAWS THAT LINE DELIBERATELY. `null` is
 * "did not ask, or could not" — an older router that has no `op=purchases`, or a failed call —
 * and `[]` is "this period holds no purchases". Collapsing them would make a customer whose
 * history failed to load read it as a month in which they bought nothing.
 *
 * ⚠ It reads the reply STRUCTURALLY rather than trusting a shape. `callTool` has already
 * unwrapped the payload, so the array is at `purchases` — the same unwrapping `creditPositionFrom`
 * relies on, and the app#173 trap one tool over.
 */
export const creditPurchasesFrom = (res: unknown): CreditPurchase[] | null => {
  if (res === null || typeof res !== 'object' || !('purchases' in res)) return null
  const rows = (res as { purchases: unknown }).purchases
  if (!Array.isArray(rows)) return null
  return rows.map(r => camelcaseKeys(r as Record<string, unknown>)) as CreditPurchase[]
}

/**
 * How many purchase rows the panel draws before it stops and says how many are left.
 *
 * ⚠⚠ THE ROUTER'S CAP IS 500 AND IT IS NOT A PAGE SIZE. `db.MaxCreditPurchaseRows` is a stated
 * ceiling, newest first, and the router names the case that reaches it: an org with auto-reload on
 * and a card that keeps declining writes one FAILED row per 15-minute sweep — about 2,900 in a
 * month. So the customer most likely to open this list is the one it is longest for, and drawing
 * every row would put 500 of them, three nodes each, inline on the billing page.
 *
 * ⚠ It is a CAP and not paging, for the same reason the router's is: what a customer needs from
 * this list is the last few charges and a true count of the rest. The count is stated rather than
 * implied, so a truncated list never reads as the whole of the month.
 */
export const MAX_PURCHASE_ROWS = 12

/**
 * What to call a purchase's status on screen.
 *
 * ⚠ `pending` says what is HAPPENING rather than naming the state: "pending" is a word about our
 * database, and the customer's question is whether their money has gone. ⚠ An unknown status
 * renders neutrally — the router owns this vocabulary and can add to it without an app deploy,
 * the rule `grantLabel` already follows.
 */
export const purchaseStatusLabel = (status: string): string => {
  switch (status) {
    case 'succeeded':
      return 'Paid'
    case 'pending':
      return 'In flight'
    case 'failed':
      return 'Not charged'
    default:
      return status
  }
}


/**
 * The automatic refill that was refused most recently, or null when the rule's last attempt was
 * not a refusal (app#271).
 *
 * ⚠⚠ THE CAPTION UNDER THE SWITCH READ "Buys $5.00 whenever…" TEN SECONDS AFTER A DECLINE. The
 * failure was on the panel — in the purchase list, below the rule, below the gauge — and the
 * sentence that describes the rule said nothing. The cap ladder (app#223, app#250) covers a rule
 * that CANNOT buy for a reason the owner chose; a declining card is the reason they did not
 * choose and cannot see, and it repeats every fifteen minutes until the balance runs out.
 *
 * ⚠ IT ASKS THE RULE'S OWN LAST ATTEMPT, not the newest row of any kind. A manual purchase after
 * the decline does not change what the rule last did. The rows are newest first, the router's
 * order — the same order the purchase list draws them in, so the row this names is the one at the
 * top of that list.
 *
 * ⚠ A refusal is not a stopped rule. nobgp-router#1307 adds a back-off; today the rule retries.
 * The caller must not say the rule has stopped.
 */
export const refusedRefill = (purchases: CreditPurchase[] | null): CreditPurchase | null => {
  const last = purchases?.find(p => p.kind === 'auto_reload')
  return last?.status === 'failed' ? last : null
}
